import * as React            from "react";
import {cx}                  from "emotion";

import "./tab-property-photos.scss";

import {consumeContext}      from "@Common/react/context";
import {ComponentLink,
        Link,
        makeChain}           from "@Common/react/cst2";
import {Invoke}              from "@Common/react/invoke";
import {Load2}               from "@Common/react/load2";
import {SessionStorageState} from "@Common/react/session-storage-state";
import {UseRef}              from "@Common/react/use-ref";
import {UseState}            from "@Common/react/use-state";
import {Types}               from "@Common/types";

import {ErrorMessage}      from "../components/error-message";
import {FolderNav}         from "../components/folder-nav";
import {GalleryImage}      from "../components/gallery-image";
import {LoadingSpinner}    from "../components/loading-spinner";
import {MultiSelectFooter} from "../components/multi-select-footer";
import {NoticeMessage}     from "../components/notice-message";
import {Paginator}         from "../components/paginator";


export const TabPropertyPhotos = ({
    modal,
    viewType,
    numResultsPerPage,
    imageArea             = null,
    singleSelectOptions   = null,
    multiSelectOptions    = null,
    orderId,
    isMobile,
    disallowSvgImages     = false,
    onPaginateNumResults,
}:{
    modal                 : Types.Common.UiComponents.ModalService3.ModalService3Next<Types.Common.UiComponents.PhotoDashboard.Models.PhotoDashboardData>,
    viewType              : Types.Common.UiComponents.PhotoDashboard.viewTypes,
    numResultsPerPage     : Types.Common.UiComponents.PhotoDashboard.numResultsPerPage,
    imageArea            ?: Types.Common.UiComponents.PhotoDashboard.Models.ImageArea,
    singleSelectOptions  ?: Types.Common.UiComponents.PhotoDashboard.Models.SingleSelectOptions,
    multiSelectOptions   ?: Types.Common.UiComponents.PhotoDashboard.Models.MultiSelectOptions,
    orderId               : number,
    isMobile              : boolean,
    disallowSvgImages    ?: boolean,
    onPaginateNumResults  : Types.Common.UiComponents.PhotoDashboard.Functions.OnPaginateNumResults,
}) => (

    makeChain<JSX.Element>()

    .then(...consumeContext("apis"))
    .then(...consumeContext("company"))

    // set state vars
    .then("state", (_, next:ComponentLink<typeof UseState<{
        isImageLoadingToEditor  : boolean,
        errorMessage            : string,
        pageNumber              : number,
    }>>) => UseState({
        defaultValue: {
            isImageLoadingToEditor: false,
            errorMessage: "",
            pageNumber: 1,
        },
        next
    }))

    // set state vars to be held in browser session storage
    .then("statePersisted", (_, next:ComponentLink<typeof UseState<{
        folderId  : number,
    }>>) => SessionStorageState({
        storageKey: "propertyPhotosSelections",
        defaultData: {
            folderId: 0,
        },
        next,
    }))

    // display loader while applying images to the editor
    .then(({state}, next:Link<JSX.Element>) => {

        if (state.value.isImageLoadingToEditor) {
            return (
                <div className="tab-content property-photos-results">
                    <div className="results">
                        <LoadingSpinner text="Applying Image"/>
                    </div>
                </div>
            );
        }

        return next();
    })

    // load folders
    .then("loaderFolders", ({apis}, next:ComponentLink<typeof Load2<Types.Apis.Models.GalleryFolderProperty[]>>) => Load2({
        _key: "folders",
        fetch: () => apis.next.gallery.propertyPhotos.getFolders(),
        captureException: true,
        next,
    }))

    // load images
    .then("loaderPhotos", ({apis, statePersisted}, next:ComponentLink<typeof Load2<Types.Apis.Models.GalleryImage[]>>) => Load2({
        _key: "images_" + statePersisted.value.folderId,
        fetch: () => statePersisted.value.folderId && apis.next.gallery.propertyPhotos.getPhotos(statePersisted.value.folderId),
        captureException: true,
        next,
    }))

    // check if pagination is required
    .then("includePagination", ({loaderPhotos}, next:Link<JSX.Element, boolean>) => next(isMobile && !!loaderPhotos.value?.length))

    // auto-select the first returned folder if no folder already selected and folders are available
    .then(({statePersisted, loaderFolders}, next:Link<JSX.Element>) => {

        // clear the folder ID if folders loaded and the ID not in the data set - this can happen when changing accounts
        // in the session
        let folderId = statePersisted.value.folderId;
        if (folderId
            && loaderFolders.value?.length
            && !loaderFolders.value?.find(x => x.id == folderId)
            && !loaderFolders.value?.find(x => x.subFolders.find(y => y.id == folderId))
        ) folderId = 0;

        // if the folder is known, skip auto-selection
        if (folderId || !loaderFolders.value?.length) return next();

        // select the first folder
        return Invoke({
            fn: () => statePersisted.onChange({
                ...statePersisted.value,
                folderId: loaderFolders.value[0].id
            })
        });

    })

    // create the results area ref hook for controlling scroll position
    .then("resultsAreaRef", (_, next:Link<JSX.Element, Types.Common.React.UseRef.UseRefNext>) => UseRef({next}))

    // render the tab contents
    .then(({state, statePersisted, apis, company, loaderFolders, loaderPhotos, includePagination, resultsAreaRef}) => {

        let results;
        if (loaderFolders.loading) results = <LoadingSpinner text="Loading Folders"/>;
        else if (!loaderFolders.value?.length) results = <NoticeMessage text="There are currently no photos saved to your company image gallery."/>;
        else if (loaderPhotos.loading) results = <LoadingSpinner text="Loading Photos"/>;
        else {

            let photos = loaderPhotos.value;
            if (includePagination) photos = photos.slice((state.value.pageNumber-1)*numResultsPerPage, state.value.pageNumber*numResultsPerPage);

            // add a message for empty folders
            if (!photos.length) results = <NoticeMessage text="There are currently no photos saved to this folder."/>;

            // display the images when available
            else results = <>
                {photos.map((photo) => (
                <GalleryImage
                    key={photo.id}
                    id={photo.id}
                    imageName={photo.name}
                    imageAttributionData={photo.attribution}
                    thumbnailUrl={photo.thumbnailUrl}
                    viewType={viewType}
                    fileType={photo.fileType}
                    fileSize={photo.fileSize}
                    width={photo.width}
                    height={photo.height}
                    areaWidth={imageArea?.widthInches}
                    areaHeight={imageArea?.heightInches}
                    photoQualitySettings={imageArea?.photoQualitySettings}
                    allowLowQuality={company.prefs.allowLowQualityPhotosProperty}
                    disabled={photo.fileType === "svg" && disallowSvgImages}
                    selectedIndex={(!multiSelectOptions) ? 0 : multiSelectOptions.selectedImages.findIndex(x => x.assetType === "order" && x.originalImageId === photo.id)+1}
                    onClick={async () => {

                        // apply the image for single select mode
                        if (singleSelectOptions) {

                            // set the image as applying
                            state.onChange({...state.value, isImageLoadingToEditor: true, errorMessage: ""});
                            if( modal.params.adminTool ) {
                                // apply the image
                                singleSelectOptions.onApply(imageArea, "order", photo.id, photo.printUrl, modal.close);
                            }
                            else {
                                // copy the image to the order gallery
                                const savedImage = await apis.next.gallery.orders.copy({orderId, imageSource: "company_gallery", imageId: photo.id});
                                if (savedImage.errorMessage) {
                                    state.onChange({...state.value, isImageLoadingToEditor: false, errorMessage: "There was an error loading your photo. Please try another."});
                                    return;
                                }

                                // apply the image
                                singleSelectOptions.onApply(imageArea, "order", savedImage.id, savedImage.printUrl, modal.close);
                            }
                        }

                        // select the image for multi-select mode
                        else if (multiSelectOptions) {

                            // remove the current selection if already selected
                            const existingSelection = multiSelectOptions.selectedImages.find(x => x.assetType === "order" && x.originalImageId === photo.id);
                            if (existingSelection) return multiSelectOptions.onSelect(existingSelection);

                            // TODO: Don't copy image until finalizing the import - This creates a delay and will create a new order gallery image for selection if they select-unselect-select-etc
                            // copy the image to the order gallery
                            const savedImage = await apis.next.gallery.orders.copy({orderId, imageSource: "company_gallery", imageId: photo.id});
                            if (savedImage.errorMessage) return;

                            // select the image
                            multiSelectOptions.onSelect({
                                assetType: "order",
                                imageId: savedImage.id,
                                originalImageId: photo.id,
                                imagePath: savedImage.printUrl,
                                thumbnailUrl: savedImage.thumbnailUrl,
                            });

                        }

                    }}
                />
                ))}
            </>;

        }

        return <>
            <div className={cx("tab-property-photos-pti tab-content property-photos-results", "viewtype-"+viewType)}>
                {loaderFolders.value && (
                <div className="sidebar no-padding">
                    <FolderNav
                        folderId={statePersisted.value.folderId}
                        folders={loaderFolders.value}
                        displayAsDropDown={isMobile}
                        onSelect={folderId => {
                            statePersisted.onChange({...statePersisted.value, folderId});
                            state.onChange({...state.value, pageNumber: 1});
                        }}
                    />
                </div>
                )}
                <div className="results"
                    ref={ref => resultsAreaRef.set(ref)}>
                    <ErrorMessage text={state.value.errorMessage}/>
                    <div className="source-images">
                        {results}
                    </div>
                </div>
                {includePagination && (
                    <Paginator
                        numItems={loaderPhotos.value?.length}
                        pageNumber={state.value.pageNumber}
                        itemsPerPage={numResultsPerPage}
                        onPaginateNumResults={numResults => {
                            resultsAreaRef.get().scrollTop = 0;
                            onPaginateNumResults(numResults);
                        }}
                        onPaginatePageNumber={pageNumber => {
                            resultsAreaRef.get().scrollTop = 0;
                            state.onChange({...state.value, pageNumber});
                        }}
                    />
                )}
            </div>
            {multiSelectOptions && (
            <MultiSelectFooter
                maxSelections={multiSelectOptions.maxSelections}
                numSelected={multiSelectOptions.selectedImages.length}
                onApply={() => {
                    state.onChange({...state.value, isImageLoadingToEditor: true});
                    multiSelectOptions.onApply(imageArea, multiSelectOptions.selectedImages, modal.close);
                }}
                onClear={multiSelectOptions.onClearSelections}
            />
            )}
        </>;

    })

    .run()

);
